import {
    query,
    Query,
    limit,
    startAfter,
    getDocs,
    getCountFromServer,
    QueryDocumentSnapshot,
    DocumentData,
} from 'firebase/firestore';
import { withErrorHandling } from './errorHandling';

/**
 * Paginated result type for Firestore queries
 */
export interface PaginatedResult<T> {
    items: T[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
    hasMore: boolean;
    totalCount?: number;
}

/**
 * Options for pagination
 */
export interface PaginationOptions {
    pageSize?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
    includeCount?: boolean;
}

/**
 * Execute a paginated Firestore query with consistent behavior
 * 
 * @template T - The type of items in the result
 * @param baseQuery - Base Firestore query (with where/orderBy constraints)
 * @param options - Pagination options
 * @returns Paginated result with items, pagination state, and optional total count
 * 
 * @example
 * ```typescript
 * const clientsRef = collection(db, `firms/${firmId}/clients`);
 * const baseQuery = query(clientsRef, where('caseStatus', '==', 'open'));
 * const result = await executePaginatedQuery<Client>(baseQuery, {
 *   pageSize: 20,
 *   includeCount: true
 * });
 * ```
 */
export async function executePaginatedQuery<T>(
    baseQuery: Query,
    options: PaginationOptions = {}
): Promise<PaginatedResult<T>> {
    return withErrorHandling(async () => {
        const { pageSize = 20, lastDoc = null, includeCount = false } = options;

        // Build paginated query
        let paginatedQuery = query(baseQuery, limit(pageSize + 1));

        if (lastDoc) {
            paginatedQuery = query(paginatedQuery, startAfter(lastDoc));
        }

        // Execute query
        const snapshot = await getDocs(paginatedQuery);
        const docs = snapshot.docs;

        // Check if there are more results
        const hasMore = docs.length > pageSize;

        // Get items (excluding the extra one for hasMore check)
        const items = docs.slice(0, pageSize).map(doc => doc.data() as T);

        // Get last document for next pagination
        const lastDocument = docs.length > 0 ? docs[Math.min(pageSize - 1, docs.length - 1)] : null;

        // Optionally get total count
        let totalCount: number | undefined;
        if (includeCount) {
            const countSnapshot = await getCountFromServer(baseQuery);
            totalCount = countSnapshot.data().count;
        }

        return {
            items,
            lastDoc: lastDocument,
            hasMore,
            totalCount,
        };
    }, 'execute paginated query') as Promise<PaginatedResult<T>>;
}
